/**
 * Utility functions for handling prefetch operations with error handling
 */

import { defaultSerializeQueryArgs } from "@reduxjs/toolkit/query";
import log from "loglevel";

/**
 * Serialize query args excluding prefetch parameter from cache key
 * This ensures prefetched and actual queries share the same cache
 * @param {Object} param0 - Object containing queryArgs and endpointName
 * @param {Object} param0.queryArgs - The query arguments
 * @param {string} param0.endpointName - The endpoint name
 * @returns {string} - Serialized query args with prefetch parameter excluded
 */
export const serializeQueryArgsExcludePrefetch = ({
  queryArgs,
  endpointName,
}) => {
  // Exclude prefetch parameters from cache key to ensure prefetched and actual queries share the same cache
  const cacheKeyArgs = { ...queryArgs };
  delete cacheKeyArgs.isPrefetchQuery;

  // Use default serialization for the remaining args
  return defaultSerializeQueryArgs({ queryArgs: cacheKeyArgs, endpointName });
};

/**
 * Safely initiates a prefetch operation, bypassing auth error handling
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} initiateFn - RTK Query initiate function
 * @param {Object} params - Parameters to pass to the initiate function
 * @param {Object} options - Additional options for the initiate call
 * @returns {Promise} - Promise that resolves regardless of success/failure
 */
export const safePrefetch = async (
  dispatch,
  initiateFn,
  params,
) => {
  try {
    // Add isPrefetchQuery: true to params for baseApi auth error handling
    // but we'll handle cache key differences in the API endpoints
    const result = dispatch(
      initiateFn(
        { ...params, isPrefetchQuery: true },
        {
          subscribe: false,
        }
      )
    );

    return result;
  } catch (error) {
    // Silently ignore any errors that occur during prefetch
    log.debug("Prefetch operation failed (ignored):", error);
    return null;
  }
};
